import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Rocket, CreditCard, CheckCircle, ArrowLeft } from 'lucide-react';

interface ExpiredDemoConversionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  demoCompanyId: string;
  userEmail: string;
}

type Step = 'create-company' | 'choose-activation' | 'success';

export function ExpiredDemoConversionModal({
  open,
  onOpenChange,
  demoCompanyId,
  userEmail
}: ExpiredDemoConversionModalProps) {
  const [currentStep, setCurrentStep] = useState<Step>('create-company');
  const [companyName, setCompanyName] = useState('');
  const [companyCnpj, setCompanyCnpj] = useState('');
  const [loading, setLoading] = useState(false);
  const [activationType, setActivationType] = useState<'trial' | 'purchase' | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const formatCnpj = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 14) {
      return numbers
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return value;
  };

  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCnpj(e.target.value);
    setCompanyCnpj(formatted);
  };

  const handleCreateCompany = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!companyName || companyName.trim().length < 3) {
      toast({
        title: "Nome inválido",
        description: "O nome da empresa deve ter pelo menos 3 caracteres",
        variant: "destructive"
      });
      return;
    }

    setCurrentStep('choose-activation');
  };

  const handleConvert = async (type: 'trial' | 'purchase') => {
    setActivationType(type);
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('convert-demo-user', {
        body: {
          demo_company_id: demoCompanyId,
          new_company_name: companyName,
          new_company_cnpj: companyCnpj || null,
          activation_type: type
        }
      });

      if (error || data.error) {
        throw new Error(error?.message || data.error);
      }

      setCurrentStep('success');
      
      toast({
        title: type === 'trial' ? "Trial ativado com sucesso!" : "Empresa criada com sucesso!",
        description: type === 'trial' 
          ? `${data.trials_activated} sistema(s) ativado(s) por 30 dias.`
          : "Escolha seus planos para começar."
      });

      setTimeout(() => {
        const redirectPath = type === 'trial'
          ? `/company/${data.company_slug}/admin`
          : `/company/${data.company_slug}/plans`;
        
        window.location.href = redirectPath;
      }, 2000);

    } catch (err: any) {
      console.error('Error converting demo to company:', err);
      toast({
        title: "Erro ao converter conta",
        description: err.message || "Ocorreu um erro. Tente novamente.",
        variant: "destructive"
      });
      setLoading(false);
      setActivationType(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-2xl [&>button]:hidden" 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {currentStep === 'success' ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <CheckCircle className="w-16 h-16 text-green-600" />
            <DialogTitle className="text-2xl">
              {activationType === 'trial' ? 'Trial Ativado!' : 'Empresa Criada!'}
            </DialogTitle>
            <DialogDescription className="text-center">
              {activationType === 'trial' 
                ? 'Sua conta foi convertida com sucesso. Redirecionando para o painel administrativo...'
                : 'Sua empresa foi criada. Redirecionando para seleção de planos...'}
            </DialogDescription>
            <Loader2 className="w-8 h-8 animate-spin text-primary mt-4" />
          </div>
        ) : currentStep === 'choose-activation' ? (
          <>
            <DialogHeader>
              <DialogTitle>Escolha Como Ativar Sua Empresa</DialogTitle>
              <DialogDescription>
                Selecione a melhor opção para começar a usar o PortalONE Suite
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 md:grid-cols-2 py-4">
              <Card className="border-2 hover:border-primary transition-colors cursor-pointer">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                    <Rocket className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Trial Gratuito</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Experimente todos os sistemas por 30 dias, sem compromisso
                    </p>
                    <ul className="text-sm space-y-2 mb-4">
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                        Acesso completo por 30 dias
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                        Todos os sistemas incluídos
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                        Sem necessidade de cartão
                      </li>
                    </ul>
                  </div>
                  <Button 
                    className="w-full"
                    onClick={() => handleConvert('trial')}
                    disabled={loading}
                  >
                    {loading && activationType === 'trial' ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Ativando...
                      </>
                    ) : (
                      'Iniciar Trial Gratuito'
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary transition-colors cursor-pointer">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                    <CreditCard className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Comprar Licença</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Adquira licenças permanentes e comece imediatamente
                    </p>
                    <ul className="text-sm space-y-2 mb-4">
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                        Planos flexíveis
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                        Suporte dedicado
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                        Recursos completos
                      </li>
                    </ul>
                  </div>
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => handleConvert('purchase')}
                    disabled={loading}
                  >
                    {loading && activationType === 'purchase' ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Criando...
                      </>
                    ) : (
                      'Ver Planos e Preços'
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-start pt-4">
              <Button 
                variant="ghost" 
                onClick={() => setCurrentStep('create-company')}
                disabled={loading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Período de Demonstração Expirado</DialogTitle>
              <DialogDescription>
                Sua demonstração chegou ao fim. Crie sua empresa para continuar usando o PortalONE Suite.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleCreateCompany} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={userEmail}
                  disabled
                  className="bg-muted"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyName">Nome da Empresa *</Label>
                <Input
                  id="companyName"
                  placeholder="Ex: Minha Empresa Ltda"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  minLength={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyCnpj">CNPJ (opcional)</Label>
                <Input
                  id="companyCnpj"
                  placeholder="00.000.000/0000-00"
                  value={companyCnpj}
                  onChange={handleCnpjChange}
                  maxLength={18}
                />
              </div>

              <Button type="submit" className="w-full">
                Continuar
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
